"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { getClientFunctions } from "@/lib/firebase-client";
import type { AccountTier, UserProfileDocument } from "@/lib/types";
import { httpsCallable } from "firebase/functions";
import { Crown, Loader2, Save } from "lucide-react";
import { useState } from "react";

type UpdateUserPayload = {
  userId: string;
  updates: {
    displayName?: string;
    email?: string;
    accountTier?: AccountTier;
    isBetaUser?: boolean;
    isAdmin?: boolean;
  };
};

type GrantFreeProPayload = {
  userId: string;
  tier: "full_pro";
};

type EditUserDialogProps = {
  user: UserProfileDocument;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserUpdated: () => void;
};

export function EditUserDialog({ user, open, onOpenChange, onUserUpdated }: EditUserDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState(user.displayName || "");
  const [email, setEmail] = useState(user.email || "");
  const [accountTier, setAccountTier] = useState<AccountTier | undefined>(user.accountTier);
  const [isBetaUser, setIsBetaUser] = useState(user.isBetaUser || false);
  const [isAdmin, setIsAdmin] = useState(user.isAdmin || false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const functions = getClientFunctions();
      if (!functions) {
        throw new Error("Firebase functions are not available");
      }

      const updateUser = httpsCallable<UpdateUserPayload, { success: boolean }>(
        functions,
        "updateUser",
      );

      const updates: UpdateUserPayload["updates"] = {};

      if (displayName.trim() !== user.displayName) {
        updates.displayName = displayName.trim();
      }
      if (email.trim().toLowerCase() !== user.email.toLowerCase()) {
        updates.email = email.trim().toLowerCase();
      }
      if (accountTier !== user.accountTier) {
        updates.accountTier = accountTier;
      }
      if (isBetaUser !== user.isBetaUser) {
        updates.isBetaUser = isBetaUser;
      }
      if (isAdmin !== user.isAdmin) {
        updates.isAdmin = isAdmin;
      }

      if (Object.keys(updates).length === 0) {
        toast({
          title: "No changes",
          description: "No changes were made to the user profile.",
        });
        onOpenChange(false);
        return;
      }

      await updateUser({
        userId: user.id!,
        updates,
      });

      toast({
        title: "User updated",
        description: "User profile has been updated successfully.",
      });

      onUserUpdated();
    } catch (error) {
      console.error("Failed to update user", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update user profile.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGrantFreePro = async () => {
    setLoading(true);
    try {
      const functions = getClientFunctions();
      if (!functions) {
        throw new Error("Firebase functions are not available");
      }

      const grantFreePro = httpsCallable<GrantFreeProPayload, { success: boolean; tier: string; isBetaUser: boolean }>(
        functions,
        "grantFreePro",
      );

      await grantFreePro({
        userId: user.id!,
        tier: "full_pro",
      });

      toast({
        title: "Free Pro tier granted",
        description: "User has been granted Full Pro tier for free.",
      });

      onUserUpdated();
    } catch (error) {
      console.error("Failed to grant free pro tier", error);
      toast({
        variant: "destructive",
        title: "Grant failed",
        description: error instanceof Error ? error.message : "Failed to grant free pro tier.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-headline">Edit User Profile</DialogTitle>
          <DialogDescription>Update user information, account tier, and permissions.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="display-name">Display Name</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="User's display name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
          </div>

          {/* Account Tier */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="account-tier">Account Tier</Label>
              <Select
                value={accountTier || "free"}
                onValueChange={(value) => setAccountTier(value as AccountTier)}
              >
                <SelectTrigger id="account-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="full_pro">Full Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Grant Free Pro Tier */}
            {accountTier && accountTier !== "free" && (
              <div className="rounded-md border bg-muted/40 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  <Label className="text-base font-semibold">Grant Free Pro Tier</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Grant this user a free pro tier subscription without charging them. This is useful for beta users.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGrantFreePro}
                  disabled={loading || (accountTier === "full_pro" && isBetaUser)}
                >
                  Grant Full Pro (Free)
                </Button>
                {isBetaUser && (
                  <Badge variant="outline" className="w-fit">
                    Beta User - Free Pro Tier Active
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Permissions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="beta-user">Beta User</Label>
                <p className="text-xs text-muted-foreground">
                  Mark user as beta user (eligible for free pro tier)
                </p>
              </div>
              <Switch
                id="beta-user"
                checked={isBetaUser}
                onCheckedChange={setIsBetaUser}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="admin">Admin</Label>
                <p className="text-xs text-muted-foreground">
                  Grant admin privileges to this user
                </p>
              </div>
              <Switch
                id="admin"
                checked={isAdmin}
                onCheckedChange={setIsAdmin}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

